import { useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import Layout from '@/components/Layout';
import NextActionBanner from '@/components/NextActionBanner';
import { API } from '@/App';
import { 
  Briefcase, CheckSquare, Clock, AlertTriangle, Package, FileText, 
  TrendingUp, Plus, Lock, IndianRupee, ArrowRight, Layers, ChevronRight, Calendar, AlertCircle,
  Receipt, Download, Eye, Wallet, CheckCircle
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Link, useNavigate } from 'react-router-dom';
import { Badge } from '@/components/ui/badge';
// iter-361: Featured Brand Partner placements on the Vendor Dashboard.
import SponsorBadge from '@/components/SponsorBadge';
import { VendorInsights } from '@/components/vendor/VendorInsights';

/**
 * VENDOR DASHBOARD - PHASE 1
 * 
 * PRIMARY FOCUS: My Rate Submissions
 * 
 * SECURITY: This dashboard shows ONLY data that belongs to the vendor:
 * - Rate contracts they have submitted
 * - Projects they are assigned to (optional)
 * - Tasks assigned to them (limited view)
 * - Tasks nearing deadline (P0 NEW)
 * - Vendor Fund Ledger (P0 NEW - VIEW ONLY)
 * 
 * NO global metrics, NO budget info, NO other vendors' data
 */

// iter-386u-k — 'Display Name' setting moved to /vendor/settings.
// 'Ad-hoc Submission Link' widget moved to /vendor-submissions.

export default function VendorDashboard() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [myContracts, setMyContracts] = useState([]);
  const [myRates, setMyRates] = useState([]);
  const [myTasks, setMyTasks] = useState([]);
  const [myProjects, setMyProjects] = useState([]);
  const [categories, setCategories] = useState({ standard_categories: [], custom_categories: [] });
  const [nearingDeadlineTasks, setNearingDeadlineTasks] = useState([]);
  const [paymentHistory, setPaymentHistory] = useState([]);
  const [fundLedgers, setFundLedgers] = useState([]); // NEW: Vendor fund ledgers per project
  // iter-386u-e — My Open Orders widget (outstanding payouts summary).
  const [openOrders, setOpenOrders] = useState({ total_pending: 0, total_paid: 0, pending_count: 0, preview: [] });
  // iter-386u-r — Project Ledger + Recent Transactions widgets (aggregate view).
  const [ledgerAggregate, setLedgerAggregate] = useState({
    total_approved: 0,
    total_billed: 0,
    total_received: 0,
    balance_pending: 0,
    transactions: [], // { date, project_name, project_id, kind: 'bill'|'payment', title, amount }
  });
  const user = JSON.parse(localStorage.getItem('user') || '{}');

  useEffect(() => {
    fetchVendorData();
  }, []);

  const fetchVendorData = async () => {
    try {
      const [contractsRes, ratesRes, categoriesRes, tasksRes, projectsRes, deadlineRes, openOrdersRes] = await Promise.all([
        axios.get(`${API}/vendor/contracts`).catch(() => ({ data: [] })),
        axios.get(`${API}/vendor/my-rates`).catch(() => ({ data: [] })),
        axios.get(`${API}/vendor/categories`).catch(() => ({ data: { standard_categories: [], custom_categories: [] } })),
        axios.get(`${API}/vendor/tasks`).catch(() => ({ data: [] })), // P1 FIX: Use vendor-specific endpoint for accurate task data
        axios.get(`${API}/projects`).catch(() => ({ data: [] })),
        axios.get(`${API}/tasks/nearing-deadline`).catch(() => ({ data: [] })),
        axios.get(`${API}/vendor/dashboard/open-orders`).catch(() => ({ data: { total_pending: 0, total_paid: 0, pending_count: 0, preview: [] } })),
      ]);

      setMyContracts(contractsRes.data);
      setMyRates(ratesRes.data);
      setCategories(categoriesRes.data);
      setMyTasks(tasksRes.data);
      setMyProjects(projectsRes.data);
      setNearingDeadlineTasks(deadlineRes.data || []);
      setOpenOrders(openOrdersRes.data || { total_pending: 0, total_paid: 0, pending_count: 0, preview: [] });
      
      // Fetch payment history for vendor across all projects
      if (projectsRes.data.length > 0) {
        const paymentPromises = projectsRes.data.map(p => 
          axios.get(`${API}/projects/${p.id}/payment-ledger`).catch(() => ({ data: null }))
        );
        const paymentResults = await Promise.all(paymentPromises);
        const allVendorPayments = paymentResults
          .filter(r => r.data && r.data.vendor_payments)
          .flatMap(r => r.data.vendor_payments.map(p => ({ ...p, project_name: r.data.project_name })))
          .filter(p => p.status !== 'cancelled')
          .sort((a, b) => new Date(b.created_at) - new Date(a.created_at));
        setPaymentHistory(allVendorPayments.slice(0, 10));
        
        // Fetch vendor fund ledgers for each project
        const fundLedgerPromises = projectsRes.data.map(p =>
          axios.get(`${API}/projects/${p.id}/fund-ledger/vendor-view`).catch(() => ({ data: null }))
        );
        const fundLedgerResults = await Promise.all(fundLedgerPromises);
        const validLedgers = fundLedgerResults
          .filter(r => r.data && r.data.project_id)
          .map(r => r.data);
        setFundLedgers(validLedgers);

        // iter-386u-r — Project Ledger + Recent Transactions widgets.
        // Aggregate every project ledger into a single view.
        const ledgerPromises = projectsRes.data.map(p =>
          axios.get(`${API}/vendor/projects/${p.id}/ledger`).catch(() => ({ data: null }))
        );
        const ledgerResults = await Promise.all(ledgerPromises);
        let agg = { total_approved: 0, total_billed: 0, total_received: 0, balance_pending: 0 };
        let txns = [];
        ledgerResults.forEach((r, idx) => {
          if (!r.data || !r.data.summary) return;
          const s = r.data.summary;
          const projName = r.data.project?.name || projectsRes.data[idx]?.name;
          const projId = r.data.project?.id || projectsRes.data[idx]?.id;
          agg.total_approved += s.total_approved || 0;
          agg.total_billed += s.total_billed || 0;
          agg.total_received += s.total_received || 0;
          agg.balance_pending += s.balance_pending || 0;
          (r.data.bills || []).forEach(b => txns.push({
            id: b.id || b.token,
            date: b.created_at,
            project_name: projName,
            project_id: projId,
            kind: 'bill',
            title: b.title || `RA Bill #${b.submission_number || (b.token || '').slice(0, 8)}`,
            amount: b.total_amount,
          }));
          (r.data.payments || []).forEach(p => txns.push({
            id: p.id,
            date: p.created_at || p.payment_date,
            project_name: projName,
            project_id: projId,
            kind: 'payment',
            title: p.notes || 'Payment received',
            amount: p.amount,
          }));
        });
        txns.sort((a, b) => new Date(b.date || 0) - new Date(a.date || 0));
        setLedgerAggregate({ ...agg, transactions: txns.slice(0, 8) });
      }
    } catch (error) {
      console.error('Failed to load dashboard:', error);
      toast.error('Failed to load dashboard');
    } finally {
      setLoading(false);
    }
  };

  // Handle download receipt
  const handleDownloadReceipt = async (paymentId) => {
    try {
      const response = await axios.get(`${API}/vendor-payments/${paymentId}/receipt-pdf`, {
        responseType: 'blob'
      });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `vendor_receipt_${paymentId}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.parentNode.removeChild(link);
      window.URL.revokeObjectURL(url);
      toast.success('Receipt downloaded');
    } catch (error) {
      toast.error('Failed to download receipt');
    }
  };

  if (loading) {
    return (
      <Layout>
        <div className="flex items-center justify-center h-96">
          <div className="text-stone-400">Loading your dashboard...</div>
        </div>
      </Layout>
    );
  }

  // Calculate stats
  const activeContracts = myContracts.filter(c => c.status === 'locked');
  const expiredContracts = myContracts.filter(c => c.status === 'expired');
  const totalRateItems = myRates.length;
  const pendingTasks = myTasks.filter(t => t.status === 'pending_approval' || t.status === 'not_started');
  const inProgressTasks = myTasks.filter(t => t.status === 'in_progress');

  return (
    <Layout>
      <div className="space-y-6" data-testid="vendor-dashboard">
        <NextActionBanner />
        <VendorInsights fallbackName={user.name} />

        {/* iter-361: Featured Brand Partner — top banner. AI cost / rate /
            recommendation prompts NEVER read this data. */}
        <div data-testid="vendor-dashboard-brand-top">
          <SponsorBadge zone="dashboard" limit={1} variant="rich-catalogue" />
        </div>

        {/* P0 FEATURE: Tasks Nearing Deadline (Next 7 Days) - VENDOR VIEW */}
        {nearingDeadlineTasks.length > 0 && (
          <div className="apple-card p-5 border-l-4 border-orange-500" data-testid="vendor-deadline-alerts">
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-orange-100 rounded-full">
                  <AlertCircle size={20} className="text-orange-600" />
                </div>
                <div>
                  <h3 className="font-semibold text-stone-900">Your Tasks Nearing Deadline</h3>
                  <p className="text-sm text-stone-500">{nearingDeadlineTasks.length} task{nearingDeadlineTasks.length > 1 ? 's' : ''} due within 7 days</p>
                </div>
              </div>
              <Badge className={`${
                nearingDeadlineTasks.filter(t => t.urgency === 'high').length > 0 
                  ? 'bg-red-100 text-red-700 border-red-200' 
                  : 'bg-orange-100 text-orange-700 border-orange-200'
              } px-3`}>
                {nearingDeadlineTasks.filter(t => t.urgency === 'high').length} urgent
              </Badge>
            </div>
            
            <div className="space-y-2 max-h-[250px] overflow-y-auto">
              {nearingDeadlineTasks.slice(0, 8).map((task) => (
                <div 
                  key={task.id}
                  className={`flex items-center gap-3 p-3 rounded-lg cursor-pointer transition-colors ${
                    task.urgency === 'high' ? 'bg-red-50 hover:bg-red-100' :
                    task.urgency === 'medium' ? 'bg-orange-50 hover:bg-orange-100' :
                    'bg-amber-50 hover:bg-amber-100'
                  }`}
                  onClick={() => navigate('/vendor/schedule')}
                  data-testid={`vendor-deadline-task-${task.id}`}
                >
                  <div className={`w-2 h-2 rounded-full flex-shrink-0 ${
                    task.urgency === 'high' ? 'bg-red-500 animate-pulse' :
                    task.urgency === 'medium' ? 'bg-orange-500' :
                    'bg-amber-400'
                  }`}></div>
                  
                  <div className="flex-1 min-w-0">
                    <p className="font-medium text-stone-900 truncate text-sm">{task.title}</p>
                    <p className="text-xs text-stone-500 truncate">{task.project_name}</p>
                  </div>
                  
                  <div className="flex items-center gap-2 flex-shrink-0">
                    <div className="text-right">
                      <span className={`text-xs font-medium ${
                        task.urgency === 'high' ? 'text-red-600' :
                        task.urgency === 'medium' ? 'text-orange-600' :
                        'text-amber-600'
                      }`}>
                        {task.days_until_deadline === 0 ? 'Due Today!' :
                         task.days_until_deadline === 1 ? 'Tomorrow' :
                         `${task.days_until_deadline}d left`}
                      </span>
                      <p className="text-xs text-stone-400">{task.progress}% done</p>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Quick Stats - Clickable */}
        <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
          <Link to="/vendor/rates" className="apple-card p-4 cursor-pointer hover:scale-[1.02] transition-transform group">
            <div className="flex items-center gap-3 mb-2">
              <div className="p-2 bg-green-100 rounded-lg">
                <Lock className="text-green-600" size={20} />
              </div>
              <span className="text-2xl font-bold">{activeContracts.length}</span>
              <ChevronRight size={16} className="text-stone-300 group-hover:text-stone-600 ml-auto" />
            </div>
            <p className="text-sm text-stone-500">Active Contracts</p>
          </Link>

          <Link to="/vendor/rates" className="apple-card p-4 cursor-pointer hover:scale-[1.02] transition-transform group">
            <div className="flex items-center gap-3 mb-2">
              <div className="p-2 bg-blue-100 rounded-lg">
                <FileText className="text-blue-600" size={20} />
              </div>
              <span className="text-2xl font-bold">{totalRateItems}</span>
              <ChevronRight size={16} className="text-stone-300 group-hover:text-stone-600 ml-auto" />
            </div>
            <p className="text-sm text-stone-500">Total Rate Items</p>
          </Link>

          <Link to="/projects" className="apple-card p-4 cursor-pointer hover:scale-[1.02] transition-transform group">
            <div className="flex items-center gap-3 mb-2">
              <div className="p-2 bg-amber-100 rounded-lg">
                <Briefcase className="text-amber-600" size={20} />
              </div>
              <span className="text-2xl font-bold">{myProjects.length}</span>
              <ChevronRight size={16} className="text-stone-300 group-hover:text-stone-600 ml-auto" />
            </div>
            <p className="text-sm text-stone-500">Assigned Projects</p>
          </Link>

          <Link to="/calendar" className="apple-card p-4 cursor-pointer hover:scale-[1.02] transition-transform group" data-testid="vendor-calendar-widget">
            <div className="flex items-center gap-3 mb-2">
              <div className="p-2 bg-red-100 rounded-lg">
                <Calendar className="text-red-600" size={20} />
              </div>
              <span className="text-2xl font-bold">📅</span>
              <ChevronRight size={16} className="text-stone-300 group-hover:text-stone-600 ml-auto" />
            </div>
            <p className="text-sm text-stone-500">My Calendar</p>
          </Link>

          <Link to="/schedule" className="apple-card p-4 cursor-pointer hover:scale-[1.02] transition-transform group">
            <div className="flex items-center gap-3 mb-2">
              <div className="p-2 bg-purple-100 rounded-lg">
                <CheckSquare className="text-purple-600" size={20} />
              </div>
              <span className="text-2xl font-bold">{myTasks.length}</span>
              <ChevronRight size={16} className="text-stone-300 group-hover:text-stone-600 ml-auto" />
            </div>
            <p className="text-sm text-stone-500">Tasks Assigned</p>
          </Link>
        </div>

        {/* iter-386u-e — My Open Orders widget */}
        <div className="apple-card p-4" data-testid="vendor-open-orders-widget">          <div className="flex items-center justify-between mb-3">
            <div className="flex items-center gap-2">
              <div className="p-2 rounded-lg bg-emerald-100">
                <Wallet size={18} className="text-emerald-700" />
              </div>
              <div>
                <h3 className="font-semibold text-stone-900">My Open Orders</h3>
                <p className="text-[11px] text-stone-500">Outstanding payouts across all your projects</p>
              </div>
            </div>
            <div className="text-right">
              <p className="text-[10px] uppercase tracking-wider text-stone-500 font-semibold">Pending</p>
              <p className="text-xl font-bold text-emerald-700" data-testid="vendor-open-orders-total">
                ₹{Number(openOrders.total_pending || 0).toLocaleString('en-IN')}
              </p>
              <p className="text-[10px] text-stone-500">{openOrders.pending_count} order{openOrders.pending_count !== 1 ? 's' : ''}</p>
            </div>
          </div>
          {(openOrders.preview || []).length > 0 ? (            <div className="divide-y divide-stone-100">
              {openOrders.preview.map((o, i) => (
                <div
                  key={o.id || i}
                  onClick={() => o.project_id && navigate(`/vendor/schedule?project=${o.project_id}`)}
                  className="py-2 flex items-center justify-between cursor-pointer hover:bg-stone-50 px-1 rounded"
                  data-testid={`vendor-open-order-${i}`}
                >
                  <div className="min-w-0">
                    <p className="text-sm text-stone-900 truncate">{o.project_name}</p>
                    <p className="text-[10px] text-stone-500">
                      {o.status === 'approved' ? 'Approved — awaiting payout' : 'Pending approval'}
                      {o.due_date ? ` · due ${String(o.due_date).slice(0, 10)}` : ''}
                    </p>
                  </div>
                  <span className="text-sm font-bold text-emerald-700 whitespace-nowrap">
                    ₹{Number(o.amount || 0).toLocaleString('en-IN')}
                  </span>
                </div>
              ))}
            </div>
          ) : (
            <p className="text-xs text-stone-400 italic text-center py-3">
              No outstanding payouts. All caught up.
            </p>
          )}
        </div>

        {/* iter-386u-k — 'Display Name' setting relocated to /vendor/settings.
            'Ad-hoc Submission Link' relocated to /vendor-submissions. */}

        {/* iter-386u-s — Project Ledger widget (above "Grow your business") */}
        {myProjects.length > 0 && (
          <div className="apple-card p-5" data-testid="vendor-project-ledger-widget">
            <div className="flex items-center justify-between mb-3">
              <h3 className="font-semibold text-stone-900 flex items-center gap-2">
                <IndianRupee size={18} className="text-purple-600" /> Project Ledger
              </h3>
              <Link
                to={`/vendor/project-ledger/${myProjects[0].id}?tab=transactions`}
                className="text-xs text-purple-600 hover:underline flex items-center gap-1"
                data-testid="ledger-widget-view-link"
              >
                View <ChevronRight size={12} />
              </Link>
            </div>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
              <div className="p-3 bg-blue-50/60 border border-blue-100 rounded">
                <p className="text-[10px] text-stone-500 uppercase tracking-wide">Approved</p>
                <p className="text-base font-bold text-blue-700" data-testid="ledger-widget-approved">₹{Number(ledgerAggregate.total_approved).toLocaleString('en-IN')}</p>
              </div>
              <div className="p-3 bg-purple-50/60 border border-purple-100 rounded">
                <p className="text-[10px] text-stone-500 uppercase tracking-wide">Billed</p>
                <p className="text-base font-bold text-purple-700" data-testid="ledger-widget-billed">₹{Number(ledgerAggregate.total_billed).toLocaleString('en-IN')}</p>
              </div>
              <div className="p-3 bg-green-50/60 border border-green-100 rounded">
                <p className="text-[10px] text-stone-500 uppercase tracking-wide">Received</p>
                <p className="text-base font-bold text-green-700" data-testid="ledger-widget-received">₹{Number(ledgerAggregate.total_received).toLocaleString('en-IN')}</p>
              </div>
              <div className="p-3 bg-amber-50/60 border border-amber-100 rounded">
                <p className="text-[10px] text-stone-500 uppercase tracking-wide">Pending</p>
                <p className="text-base font-bold text-amber-700" data-testid="ledger-widget-pending">₹{Number(ledgerAggregate.balance_pending).toLocaleString('en-IN')}</p>
              </div>
            </div>
            <p className="text-[10px] text-stone-400 mt-3 italic">Across {myProjects.length} project{myProjects.length > 1 ? 's' : ''}</p>
          </div>
        )}

        {/* iter-386u-s — Recent Transactions (below Project Ledger) */}
        {myProjects.length > 0 && (
          <div className="apple-card p-5" data-testid="vendor-recent-transactions-widget">
            <div className="flex items-center justify-between mb-3">
              <h3 className="font-semibold text-stone-900 flex items-center gap-2">
                <Receipt size={18} className="text-emerald-600" /> Recent Transactions
              </h3>
              <span className="text-xs text-stone-500">{ledgerAggregate.transactions.length} recent</span>
            </div>
            {ledgerAggregate.transactions.length === 0 ? (
              <p className="text-sm text-stone-400 italic py-6 text-center">No RA bills or payments recorded yet.</p>
            ) : (
              <div className="space-y-1.5 max-h-56 overflow-y-auto">
                {ledgerAggregate.transactions.map((t) => (
                  <Link
                    key={`${t.kind}-${t.id}`}
                    to={`/vendor/project-ledger/${t.project_id}?tab=transactions`}
                    className="flex items-center justify-between p-2.5 rounded border border-stone-100 hover:bg-stone-50"
                    data-testid={`recent-txn-${t.id}`}
                  >
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium text-stone-800 truncate">{t.title}</p>
                      <p className="text-[11px] text-stone-500">
                        {t.project_name}  •  {t.date ? new Date(t.date).toLocaleDateString('en-IN') : '—'}  •  {t.kind === 'bill' ? 'RA Bill' : 'Payment Received'}
                      </p>
                    </div>
                    <p className={`text-sm font-semibold ${t.kind === 'payment' ? 'text-green-700' : 'text-purple-700'}`}>
                      {t.kind === 'payment' ? '+' : ''}₹{Number(t.amount || 0).toLocaleString('en-IN')}
                    </p>
                  </Link>
                ))}
              </div>
            )}
          </div>
        )}

        {/* iter-361: Featured Brand Partner — side card. */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div className="md:col-span-2 apple-card p-4 bg-gradient-to-br from-stone-50 to-white">
            <h3 className="text-sm font-semibold text-stone-700 mb-2 flex items-center gap-2">
              <TrendingUp size={14} className="text-stone-500" /> Grow your business
            </h3>
            <p className="text-xs text-stone-500">
              Keep your rates current, respond quickly to quote requests, and discover trusted brand partners we work with on live projects.
            </p>
          </div>
          <div data-testid="vendor-dashboard-brand-sidebar">
            <SponsorBadge zone="dashboard" limit={1} variant="premium-minimal" />
          </div>
        </div>

        {/* iter-386u-s — legacy widget block removed (moved above Grow Your Business). */}

        {/* Primary CTA: No Contracts Yet */}
        {myContracts.length === 0 && (
          <div className="apple-card p-8 text-center bg-gradient-to-br from-blue-50 to-indigo-50 border-blue-200">
            <Layers className="mx-auto mb-4 text-blue-500" size={56} />
            <h2 className="text-xl font-semibold text-stone-800 mb-2">
              Start Submitting Your Rates
            </h2>
            <p className="text-stone-600 mb-6 max-w-md mx-auto">
              Choose from {categories.standard_categories.length} service categories and submit your rates using our standardized templates. 
              Your rates will be visible to project owners for selection.
            </p>
            <Link to="/vendor/rates">
              <Button size="lg" className="bg-blue-600 hover:bg-blue-700">
                <TrendingUp size={18} className="mr-2" />
                Submit Rates Now
                <ArrowRight size={18} className="ml-2" />
              </Button>
            </Link>
          </div>
        )}

        {/* My Rate Contracts - PRIMARY SECTION */}
        <div className="apple-card p-6">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-lg font-semibold flex items-center gap-2">
              <FileText size={20} className="text-stone-600" />
              My Rate Submissions
            </h2>
            <Link to="/vendor/rates">
              <Button variant="outline" size="sm">
                <Plus size={16} className="mr-2" />
                New Submission
              </Button>
            </Link>
          </div>
          
          {myContracts.length === 0 ? (
            <div className="text-center py-8 text-stone-500">
              <Package className="mx-auto mb-3" size={40} />
              <p>No rate contracts submitted yet.</p>
              <p className="text-sm mt-1">Submit your rates to start receiving orders.</p>
            </div>
          ) : (
            <div className="space-y-3">
              {myContracts.map((contract) => {
                const daysRemaining = Math.max(0, Math.ceil((new Date(contract.expires_at) - new Date()) / (1000 * 60 * 60 * 24)));
                const isExpiringSoon = daysRemaining <= 7 && daysRemaining > 0;
                
                return (
                  <div key={contract.id} className="p-4 bg-stone-50 rounded-lg hover:bg-stone-100 transition-colors">
                    <div className="flex items-start justify-between">
                      <div className="flex-1">
                        <div className="flex items-center gap-2 mb-1">
                          <p className="font-medium">{contract.contract_name}</p>
                          <Badge 
                            variant="outline"
                            className={
                              contract.status === 'locked' ? 'bg-green-100 text-green-700 border-green-200' :
                              contract.status === 'expired' ? 'bg-red-100 text-red-700 border-red-200' :
                              'bg-stone-100'
                            }
                          >
                            <Lock size={10} className="mr-1" />
                            {contract.status}
                          </Badge>
                        </div>
                        <p className="text-sm text-stone-500">
                          {contract.rate_count} items • {contract.validity_period_months} month(s) validity
                        </p>
                      </div>
                      <div className="text-right">
                        <p className={`text-sm font-medium ${
                          isExpiringSoon ? 'text-amber-600' : 
                          daysRemaining === 0 ? 'text-red-600' : 
                          'text-stone-600'
                        }`}>
                          {daysRemaining > 0 ? `${daysRemaining} days left` : 'Expired'}
                        </p>
                        {isExpiringSoon && (
                          <p className="text-xs text-amber-500 flex items-center gap-1 justify-end mt-1">
                            <AlertTriangle size={12} />
                            Expiring soon
                          </p>
                        )}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>

        {/* Available Categories Quick View */}
        {myContracts.length === 0 && (
          <div className="apple-card p-6">
            <h2 className="text-lg font-semibold mb-4 flex items-center gap-2">
              <Layers size={20} className="text-stone-600" />
              Available Service Categories
            </h2>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
              {categories.standard_categories.slice(0, 8).map((cat) => (
                <div key={cat.id} className="p-3 bg-stone-50 rounded-lg text-center">
                  <p className="font-medium text-sm text-stone-800">{cat.name}</p>
                  <p className="text-xs text-stone-500 mt-1">{cat.template_count} templates</p>
                </div>
              ))}
            </div>
            {categories.standard_categories.length > 8 && (
              <p className="text-sm text-stone-500 text-center mt-3">
                + {categories.standard_categories.length - 8} more categories available
              </p>
            )}
          </div>
        )}

        {/* Assigned Projects (Secondary) */}
        {myProjects.length > 0 && (
          <div className="apple-card p-6">
            <h2 className="text-lg font-semibold mb-4 flex items-center gap-2">
              <Briefcase size={20} className="text-stone-600" />
              Your Assigned Projects
            </h2>
            
            <div className="space-y-3">
              {myProjects.slice(0, 5).map((project) => {
                const projectTasks = myTasks.filter(t => t.project_id === project.id);
                const completedCount = projectTasks.filter(t => 
                  t.status === 'completed' || t.status === 'fully_completed'
                ).length;
                const progress = projectTasks.length > 0 
                  ? Math.round((completedCount / projectTasks.length) * 100) 
                  : 0;

                return (
                  <Link 
                    key={project.id} 
                    to={`/projects/${project.id}`}
                    className="block p-4 bg-stone-50 rounded-lg hover:bg-stone-100 transition-colors"
                  >
                    <div className="flex items-center justify-between mb-2">
                      <span className="font-medium">{project.name}</span>
                      <span className="text-sm text-stone-500">{projectTasks.length} tasks</span>
                    </div>
                    <div className="flex items-center gap-3">
                      <div className="flex-1 h-2 bg-stone-200 rounded-full overflow-hidden">
                        <div 
                          className="h-full bg-green-500 rounded-full transition-all"
                          style={{ width: `${progress}%` }}
                        />
                      </div>
                      <span className="text-sm font-medium text-stone-600">{progress}%</span>
                    </div>
                  </Link>
                );
              })}
            </div>
          </div>
        )}

        {/* Recent Tasks (Compact) */}
        {myTasks.length > 0 && (
          <div className="apple-card p-6">
            <h2 className="text-lg font-semibold mb-4 flex items-center gap-2">
              <CheckSquare size={20} className="text-stone-600" />
              Your Tasks Activity
            </h2>
            
            {/* Task Status Summary - NEW */}
            <div className="grid grid-cols-3 gap-4 mb-6">
              <div className="text-center p-3 bg-stone-100 rounded-lg">
                <div className="text-2xl font-bold text-stone-600">
                  {myTasks.filter(t => t.status === 'not_started' || t.status === 'pending').length}
                </div>
                <div className="text-xs text-stone-500">Not Started</div>
              </div>
              <div className="text-center p-3 bg-blue-100 rounded-lg">
                <div className="text-2xl font-bold text-blue-600">
                  {inProgressTasks.length}
                </div>
                <div className="text-xs text-blue-600">In Progress</div>
              </div>
              <div className="text-center p-3 bg-green-100 rounded-lg">
                <div className="text-2xl font-bold text-green-600">
                  {myTasks.filter(t => t.status === 'completed').length}
                </div>
                <div className="text-xs text-green-600">Completed</div>
              </div>
            </div>
            
            {/* Progress Bar - NEW */}
            <div className="mb-4">
              <div className="flex justify-between text-sm text-stone-500 mb-1">
                <span>Overall Progress</span>
                <span>
                  {myTasks.length > 0 
                    ? Math.round((myTasks.filter(t => t.status === 'completed').length / myTasks.length) * 100)
                    : 0}%
                </span>
              </div>
              <div className="h-3 bg-stone-200 rounded-full overflow-hidden">
                <div 
                  className="h-full bg-gradient-to-r from-blue-500 to-green-500 rounded-full transition-all"
                  style={{ 
                    width: `${myTasks.length > 0 
                      ? (myTasks.filter(t => t.status === 'completed').length / myTasks.length) * 100 
                      : 0}%` 
                  }}
                />
              </div>
            </div>
            
            {/* Task List */}
            <div className="space-y-2">
              {myTasks.slice(0, 5).map((task) => (
                <Link 
                  key={task.id} 
                  to="/vendor/schedule"
                  className="flex items-center justify-between p-3 bg-stone-50 rounded-lg hover:bg-stone-100 transition-colors"
                >
                  <div className="flex-1">
                    <p className="font-medium text-sm">{task.task_title || task.name || task.title}</p>
                    <p className="text-xs text-stone-500">{task.category_name || task.category || 'Uncategorized'}</p>
                  </div>
                  <div className="flex items-center gap-3">
                    <div className="text-right text-xs text-stone-400">
                      {task.progress_percentage || 0}%
                    </div>
                    <Badge 
                      variant="outline"
                      className={
                        task.status === 'completed' ? 'bg-green-100 text-green-700' :
                        task.status === 'in_progress' ? 'bg-blue-100 text-blue-700' :
                        task.status === 'delayed' ? 'bg-red-100 text-red-700' :
                        'bg-stone-100'
                      }
                    >
                      {(task.status || 'not_started').replace(/_/g, ' ')}
                    </Badge>
                  </div>
                </Link>
              ))}
              
              {myTasks.length > 5 && (
                <Link to="/vendor/schedule" className="block text-center py-2 text-blue-600 hover:underline text-sm">
                  View all {myTasks.length} tasks →
                </Link>
              )}
            </div>
          </div>
        )}
        
        {/* Empty State for Tasks */}
        {myTasks.length === 0 && (
          <div className="apple-card p-6 text-center">
            <CheckSquare size={40} className="mx-auto mb-3 text-stone-300" />
            <h3 className="font-medium text-stone-600 mb-1">No Tasks Assigned Yet</h3>
            <p className="text-sm text-stone-500">When you're assigned to tasks, they'll appear here.</p>
          </div>
        )}

        {/* VENDOR FUND LEDGER - View Only */}
        {fundLedgers.length > 0 && (
          <div className="apple-card p-6" data-testid="vendor-fund-ledger">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-lg font-semibold flex items-center gap-2">
                <Wallet size={20} className="text-purple-600" />
                My Fund Ledger
              </h2>
              <Badge variant="outline" className="bg-purple-50 text-purple-600 border-purple-200">
                <Eye size={12} className="mr-1" />
                View Only
              </Badge>
            </div>
            
            <div className="space-y-4">
              {fundLedgers.map((ledger) => (
                <div key={ledger.project_id} className="bg-purple-50/50 rounded-lg p-4 border border-purple-200">
                  <div className="flex items-center justify-between mb-3">
                    <h3 className="font-medium text-stone-800">{ledger.project_name}</h3>
                    <Badge className={`${
                      ledger.payment_progress_percentage >= 100 ? 'bg-green-100 text-green-700 border-green-200' :
                      ledger.payment_progress_percentage >= 50 ? 'bg-purple-100 text-purple-700 border-purple-200' :
                      'bg-amber-100 text-amber-700 border-amber-200'
                    }`}>
                      {ledger.payment_progress_percentage || 0}% Received
                    </Badge>
                  </div>
                  
                  {/* Vendor Contract Summary */}
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-3">
                    <div className="p-3 bg-white rounded-lg text-center border">
                      <p className="text-xs text-stone-500 mb-1">My Contract</p>
                      <p className="text-lg font-bold text-stone-800">₹{(ledger.vendor_contract_value || 0).toLocaleString()}</p>
                    </div>
                    <div className="p-3 bg-green-50 rounded-lg text-center border border-green-100">
                      <p className="text-xs text-green-600 mb-1">Received</p>
                      <p className="text-lg font-bold text-green-700">₹{(ledger.total_received || 0).toLocaleString()}</p>
                    </div>
                    <div className="p-3 bg-amber-50 rounded-lg text-center border border-amber-100">
                      <p className="text-xs text-amber-600 mb-1">Retention Held</p>
                      <p className="text-lg font-bold text-amber-700">₹{(ledger.retention_held || 0).toLocaleString()}</p>
                    </div>
                    <div className="p-3 bg-blue-50 rounded-lg text-center border border-blue-100">
                      <p className="text-xs text-blue-600 mb-1">Pending</p>
                      <p className="text-lg font-bold text-blue-700">₹{(ledger.balance_pending || 0).toLocaleString()}</p>
                    </div>
                  </div>
                  
                  {/* Progress Bar */}
                  <div className="mb-2">
                    <div className="flex justify-between text-xs text-stone-500 mb-1">
                      <span>Payment Progress</span>
                      <span>{ledger.payment_progress_percentage || 0}%</span>
                    </div>
                    <div className="h-2 bg-stone-200 rounded-full overflow-hidden">
                      <div 
                        className="h-full bg-gradient-to-r from-purple-500 to-green-500 rounded-full transition-all"
                        style={{ width: `${ledger.payment_progress_percentage || 0}%` }}
                      />
                    </div>
                  </div>
                  
                  {/* Link to project */}
                  <Link 
                    to={`/projects/${ledger.project_id}`}
                    className="block text-center text-sm text-purple-600 hover:underline mt-2"
                  >
                    View Project Details →
                  </Link>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Vendor Payment History with Receipt Download */}
        {paymentHistory.length > 0 && (
          <div className="apple-card p-6" data-testid="vendor-payment-history">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-lg font-semibold flex items-center gap-2">
                <IndianRupee size={20} className="text-green-600" />
                My Payments Received
              </h2>
              <Link to="/vendor/payment-history" className="text-sm text-blue-600 hover:underline flex items-center gap-1">
                View All <ChevronRight size={14} />
              </Link>
            </div>
            
            <div className="space-y-3">
              {paymentHistory.map((payment) => (
                <div key={payment.id} className="p-4 bg-green-50/50 rounded-lg border border-green-100">
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <p className="font-semibold text-green-700">₹{(payment.amount || 0).toLocaleString()}</p>
                      <p className="text-sm text-stone-500">{payment.project_name}</p>
                      <p className="text-xs text-stone-400 mt-1">{new Date(payment.created_at).toLocaleString('en-IN')}</p>
                      {payment.notes && (
                        <p className="text-sm text-stone-600 mt-2 italic">"{payment.notes}"</p>
                      )}
                    </div>
                    <div className="flex flex-col items-end gap-2">
                      <Badge className="bg-green-100 text-green-700 border-green-200">
                        Received
                      </Badge>
                      <Button
                        size="sm"
                        variant="outline"
                        className="text-xs"
                        onClick={async () => {
                          try {
                            const response = await axios.get(`${API}/vendor-payments/${payment.id}/receipt-pdf`, {
                              responseType: 'blob'
                            });
                            const url = window.URL.createObjectURL(new Blob([response.data]));
                            const link = document.createElement('a');
                            link.href = url;
                            link.setAttribute('download', `vendor_receipt_${payment.id}.pdf`);
                            document.body.appendChild(link);
                            link.click();
                            link.parentNode.removeChild(link);
                            window.URL.revokeObjectURL(url);
                            toast.success('Receipt downloaded');
                          } catch (error) {
                            toast.error('Failed to download receipt');
                          }
                        }}
                      >
                        <FileText size={12} className="mr-1" />
                        Receipt
                      </Button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
        {/* iter-361: Featured Brand Partner — bottom banner. */}
        <div className="pt-2" data-testid="vendor-dashboard-brand-bottom">
          <SponsorBadge zone="dashboard" limit={1} variant="premium-minimal" />
        </div>
      </div>
    </Layout>
  );
}
